import React, { MouseEvent } from 'react';
import { Typography, Container, Button, styled, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ErrorSVG from './ErrorSVG';
import Layout from 'components/Layout';
import { useNavigate } from 'react-router-dom';
import { useResetRecoilState } from 'recoil';
import { tokenAtom } from 'atoms';

const OopsStyled = styled(Typography)({
  marginBottom: '0.6rem',
});

const TryAgainButton = styled(Button)({
  marginTop: '2.375rem',
  padding: '0.3rem 2.5rem',
});

const CauseText = styled(Typography)({
  marginTop: '0.6rem',
});
export type Error = {
  graphQLErrors?: Array<{ message: string }>;
  networkError?: { statusCode: number };
  response?: { data?: { message: string } };
};

type Props = {
  fallback?: (props: {
    isApiError: boolean;
    error?: Error;
    handlerTryAgainClick: React.MouseEventHandler;
    reloadChildren: () => void;
  }) => React.ReactNode;
  tryAgainClick?: React.MouseEventHandler;
  reloadChildrenOnTryAgain?: boolean;
  onError?: (error: Error) => void;
  children: React.ReactNode;
};

interface State {
  hasError: boolean;
  error?: Error;
  isApiError: boolean;
}
class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: undefined, isApiError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      isApiError: !!error?.graphQLErrors,
      error,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  componentDidCatch(error: any, errorInfo: any) {
    // eslint-disable-next-line no-console
    console.error(error);
    this.props.onError && this.props.onError(error);
  }

  render() {
    const { hasError, error, isApiError } = this.state;
    const { fallback, children, tryAgainClick, reloadChildrenOnTryAgain } = this.props;
    const reloadChildren = () => {
      this.setState({
        hasError: false,
        error: undefined,
        isApiError: false,
      });
    };

    function handlerTryAgainClick(e: MouseEvent) {
      if (reloadChildrenOnTryAgain) {
        reloadChildren();
      }
      if (tryAgainClick) {
        tryAgainClick(e);
        return;
      }
      window.location.reload();
    }

    if (hasError) {
      return (
        fallback?.({
          isApiError,
          error,
          handlerTryAgainClick,
          reloadChildren,
        }) || (
          <ErrorBoundaryContentLayout
            {...{ isApiError, error, handlerTryAgainClick, reloadChildren }}
          />
        )
      );
    }
    return children;
  }
}

export function ErrorBoundaryContentLayout({
  isApiError,
  error,
  handlerTryAgainClick,
  reloadChildren,
}: {
  isApiError: boolean;
  error?: Error;
  handlerTryAgainClick: React.MouseEventHandler;
  reloadChildren: () => void;
}) {
  const { t } = useTranslation(['error_boundary', 'api_errors']);
  const navigate = useNavigate();
  const resetToken = useResetRecoilState(tokenAtom);

  React.useEffect(
    function redirectToLoginWhenUnauthorized() {
      if (isApiError && error?.networkError?.statusCode === 401) {
        resetToken();
        navigate('/login');
        reloadChildren();
      }
    },
    [isApiError, error, reloadChildren, navigate, resetToken],
  );

  return (
    <Layout.Container>
      <Container sx={{ height: '100%' }}>
        <Layout.Content
          sx={{
            flexDirection: 'column',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ErrorSVG
            sx={{
              mb: 2,
              width: '162.802px',
              height: '139.544px',
              color: 'text.primary',
            }}
          />
          <OopsStyled variant="h4">Oops!</OopsStyled>
          <Typography variant="body1">{t('error_boundary:something_went_wrong')}</Typography>
          <Typography variant="body1">{t('error_boundary:problem_executing_request')}</Typography>
          {isApiError && (
            <CauseText variant="body1" color="textSecondary">
              {/* @ts-ignore */}
              {t(`api_errors:${error?.response?.data?.message || error}`)}
            </CauseText>
          )}
          <TryAgainButton variant="contained" onClick={handlerTryAgainClick}>
            {t('error_boundary:try_again')}
          </TryAgainButton>
        </Layout.Content>
      </Container>
    </Layout.Container>
  );
}

export function ErrorBoundaryContent({
  isApiError,
  error,
  handlerTryAgainClick,
  reloadChildren,
}: {
  isApiError: boolean;
  error?: Error;
  handlerTryAgainClick: React.MouseEventHandler;
  reloadChildren: () => void;
}) {
  const { t } = useTranslation(['error_boundary', 'api_errors']);
  const navigate = useNavigate();

  React.useEffect(
    function redirectToLoginWhenUnauthorized() {
      if (isApiError && error?.networkError?.statusCode === 401) {
        navigate('/login');
        reloadChildren();
      }
    },
    [isApiError, error, reloadChildren, navigate],
  );

  return (
    <Box
      sx={{
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
      }}
    >
      <ErrorSVG
        sx={{
          mb: 2,
          width: '162.802px',
          height: '139.544px',
          color: 'text.primary',
        }}
      />
      <OopsStyled variant="h4">Oops!</OopsStyled>
      <Typography variant="body1">{t('error_boundary:something_went_wrong')}</Typography>
      <Typography variant="body1">{t('error_boundary:problem_executing_request')}</Typography>
      {isApiError && (
        <CauseText variant="body1" color="textSecondary">
          {t(
            /* @ts-ignore */
            `api_errors:${
              error?.response?.data?.message || error?.graphQLErrors?.[0]?.message || error
            }`,
          )}
        </CauseText>
      )}
      <TryAgainButton variant="contained" onClick={handlerTryAgainClick}>
        {t('error_boundary:try_again')}
      </TryAgainButton>
    </Box>
  );
}

export default ErrorBoundary;
